/**
 * Render a ChartConfig to an SVG data URL for use as an image source.
 * Uses a hidden DOM element to render Recharts, then extracts the SVG markup.
 * This avoids react-dom/server which isn't available in browser bundles.
 *
 * IMPORTANT: All data components must have isAnimationActive={false},
 * otherwise flushSync captures the initial animation frame (zero-size shapes).
 */
import { createRoot } from 'react-dom/client'
import { flushSync } from 'react-dom'
import {
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  AreaChart, Area,
  ScatterChart, Scatter,
  ComposedChart,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, ZAxis, CartesianGrid, LabelList,
} from 'recharts'
import type { ChartConfig } from '../types'

const DEFAULT_COLORS = [
  '#4F81BD', '#C0504D', '#9BBB59', '#8064A2', '#4BACC6',
  '#F79646', '#2C4D75', '#772C2A', '#5F7530', '#4D3B62',
]

/**
 * Render a chart to an SVG data URL that can be used as an image source.
 * Returns `data:image/svg+xml;base64,...`
 */
export function renderChartToDataUrl(config: ChartConfig): string {
  const svg = renderChartToSvgString(config)
  const base64 = btoa(unescape(encodeURIComponent(svg)))
  return `data:image/svg+xml;base64,${base64}`
}

function renderChartToSvgString(config: ChartConfig): string {
  const {
    chartType, title, data, categoryKey, series,
    width = 600, height = 400, legend: showLegend = true, stacked = false,
    xAxisLabel, yAxisLabel,
    backgroundColor, borderColor,
    valAxisNumFmt, showDataLabels,
  } = config

  const getColor = (i: number) => series[i]?.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]

  let chartElement: React.ReactElement

  const xLabel = xAxisLabel ? { value: xAxisLabel, position: 'insideBottom' as const, offset: -5, fontSize: 11 } : undefined
  const yLabel = yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' as const, offset: 10, fontSize: 11 } : undefined

  // Build Y axis tick formatter from Excel number format
  const tickFormatter = valAxisNumFmt ? (val: number) => {
    if (valAxisNumFmt.includes('%')) return `${(val * (valAxisNumFmt.includes('0%') && val <= 1 ? 100 : 1)).toFixed(valAxisNumFmt.split('.')[1]?.replace(/[^0]/g, '').length || 0)}%`
    if (valAxisNumFmt.includes('#,##0') || valAxisNumFmt.includes(',')) return val.toLocaleString()
    return String(val)
  } : undefined

  // If any series uses a secondary Y axis, use ComposedChart (combo) rendering
  const hasSecondary = series.some(s => s.yAxisId === '1')
  let effectiveChartType = chartType
  if (hasSecondary && chartType !== 'combo' && chartType !== 'pie' && chartType !== 'doughnut' && chartType !== 'scatter' && chartType !== 'radar' && chartType !== 'bubble') {
    effectiveChartType = 'combo'
    // Tag untagged series with the original chart type so combo renders them correctly
    const fallbackType = (chartType === 'bar' || chartType === 'line' || chartType === 'area') ? chartType : 'bar'
    for (const s of series) {
      if (!s.seriesChartType) s.seriesChartType = fallbackType
    }
  }

  switch (effectiveChartType) {
    case 'bar':
      chartElement = (
        <BarChart width={width} height={height} data={data} margin={{ top: 5, right: 20, bottom: 20, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={categoryKey} label={xLabel} />
          <YAxis label={yLabel} tickFormatter={tickFormatter} />
          {/* Legend rendered manually in SVG wrapper — Recharts Legend needs DOM measurement */}
          {series.map((s, i) => (
            <Bar key={s.dataKey} dataKey={s.dataKey} name={s.name} fill={getColor(i)} stackId={stacked ? 'stack' : undefined} isAnimationActive={false}>
              {showDataLabels && <LabelList dataKey={s.dataKey} position="top" fontSize={9} />}
            </Bar>
          ))}
        </BarChart>
      )
      break

    case 'line':
      chartElement = (
        <LineChart width={width} height={height} data={data} margin={{ top: 5, right: 20, bottom: 20, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={categoryKey} label={xLabel} />
          <YAxis label={yLabel} tickFormatter={tickFormatter} />
          {/* Legend rendered manually in SVG wrapper — Recharts Legend needs DOM measurement */}
          {series.map((s, i) => (
            <Line key={s.dataKey} type="monotone" dataKey={s.dataKey} name={s.name} stroke={getColor(i)} dot={data.length <= 20} isAnimationActive={false}>
              {showDataLabels && <LabelList dataKey={s.dataKey} position="top" fontSize={9} />}
            </Line>
          ))}
        </LineChart>
      )
      break

    case 'area':
      chartElement = (
        <AreaChart width={width} height={height} data={data} margin={{ top: 5, right: 20, bottom: 20, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={categoryKey} label={xLabel} />
          <YAxis label={yLabel} tickFormatter={tickFormatter} />
          {/* Legend rendered manually in SVG wrapper — Recharts Legend needs DOM measurement */}
          {series.map((s, i) => (
            <Area key={s.dataKey} type="monotone" dataKey={s.dataKey} name={s.name} fill={getColor(i)} stroke={getColor(i)} fillOpacity={0.3} stackId={stacked ? 'stack' : undefined} isAnimationActive={false}>
              {showDataLabels && <LabelList dataKey={s.dataKey} position="top" fontSize={9} />}
            </Area>
          ))}
        </AreaChart>
      )
      break

    case 'pie':
    case 'doughnut': {
      const dataKey = series[0]?.dataKey || 'value'
      const innerRadius = chartType === 'doughnut' ? '50%' : undefined
      chartElement = (
        <PieChart width={width} height={height} margin={{ top: 5, right: 20, bottom: 20, left: 5 }}>
          {/* Legend rendered manually in SVG wrapper — Recharts Legend needs DOM measurement */}
          <Pie data={data} dataKey={dataKey} nameKey={categoryKey} cx="50%" cy="50%" innerRadius={innerRadius} outerRadius="80%" label={data.length <= 10} isAnimationActive={false}>
            {data.map((_, i) => (
              <Cell key={i} fill={DEFAULT_COLORS[i % DEFAULT_COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
      )
      break
    }

    case 'scatter':
      chartElement = (
        <ScatterChart width={width} height={height} margin={{ top: 5, right: 20, bottom: 20, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={categoryKey} name={categoryKey} type="number" label={xLabel} />
          <YAxis dataKey={series[0]?.dataKey} name={series[0]?.name} type="number" label={yLabel} />
          {/* Legend rendered manually in SVG wrapper — Recharts Legend needs DOM measurement */}
          {series.map((s, i) => (
            <Scatter key={s.dataKey} name={s.name} data={data} fill={getColor(i)} isAnimationActive={false} />
          ))}
        </ScatterChart>
      )
      break

    case 'combo': {
      const hasSecondaryAxis = series.some(s => s.yAxisId === '1')
      chartElement = (
        <ComposedChart width={width} height={height} data={data} margin={{ top: 5, right: hasSecondaryAxis ? 50 : 20, bottom: 20, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={categoryKey} label={xLabel} />
          <YAxis yAxisId="0" label={yLabel} tickFormatter={tickFormatter} />
          {hasSecondaryAxis && <YAxis yAxisId="1" orientation="right" tickFormatter={tickFormatter} />}
          {/* Legend rendered manually in SVG wrapper — Recharts Legend needs DOM measurement */}
          {series.map((s, i) => {
            const axisId = s.yAxisId || '0'
            const color = getColor(i)
            const labels = showDataLabels ? <LabelList dataKey={s.dataKey} position="top" fontSize={9} /> : null
            switch (s.seriesChartType) {
              case 'line':
                return <Line key={s.dataKey} yAxisId={axisId} type="monotone" dataKey={s.dataKey} name={s.name} stroke={color} dot={data.length <= 20} isAnimationActive={false}>{labels}</Line>
              case 'area':
                return <Area key={s.dataKey} yAxisId={axisId} type="monotone" dataKey={s.dataKey} name={s.name} fill={color} stroke={color} fillOpacity={0.3} isAnimationActive={false}>{labels}</Area>
              case 'bar':
              default:
                return <Bar key={s.dataKey} yAxisId={axisId} dataKey={s.dataKey} name={s.name} fill={color} isAnimationActive={false}>{labels}</Bar>
            }
          })}
        </ComposedChart>
      )
      break
    }

    case 'radar':
      chartElement = (
        <RadarChart width={width} height={height} data={data} margin={{ top: 5, right: 20, bottom: 20, left: 5 }}>
          <PolarGrid />
          <PolarAngleAxis dataKey={categoryKey} />
          <PolarRadiusAxis tickFormatter={tickFormatter} />
          {series.map((s, i) => (
            <Radar key={s.dataKey} dataKey={s.dataKey} name={s.name} stroke={getColor(i)} fill={getColor(i)} fillOpacity={0.3} isAnimationActive={false} />
          ))}
        </RadarChart>
      )
      break

    case 'bubble':
      chartElement = (
        <ScatterChart width={width} height={height} margin={{ top: 5, right: 20, bottom: 20, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={categoryKey} name={categoryKey} type="number" label={xLabel} />
          <YAxis dataKey={series[0]?.dataKey} name={series[0]?.name} type="number" label={yLabel} tickFormatter={tickFormatter} />
          <ZAxis dataKey={series[0]?.bubbleSizeKey || 'z'} range={[20, 400]} />
          {/* Legend rendered manually in SVG wrapper — Recharts Legend needs DOM measurement */}
          {series.map((s, i) => (
            <Scatter key={s.dataKey} name={s.name} data={data} fill={getColor(i)} isAnimationActive={false} />
          ))}
        </ScatterChart>
      )
      break

    default:
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><rect width="100%" height="100%" fill="white"/><text x="50%" y="50%" text-anchor="middle" fill="#888">Unsupported chart: ${chartType}</text></svg>`
  }

  // Render into a hidden DOM container to extract SVG markup
  const container = document.createElement('div')
  container.style.position = 'absolute'
  container.style.left = '-9999px'
  container.style.top = '-9999px'
  document.body.appendChild(container)

  let svgMarkup = ''
  try {
    const root = createRoot(container)
    flushSync(() => {
      root.render(chartElement)
    })

    const svgEl = container.querySelector('svg')
    if (svgEl) {
      // Ensure xmlns is set for standalone SVG
      svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
      // Make inner chart background transparent — outer wrapper provides bg/border
      const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
      bg.setAttribute('width', '100%')
      bg.setAttribute('height', '100%')
      bg.setAttribute('fill', 'transparent')
      svgEl.insertBefore(bg, svgEl.firstChild)

      // Inject pie chart legend directly into the SVG (right side vertical list)
      if ((chartType === 'pie' || chartType === 'doughnut') && showLegend) {
        const legendX = width * 0.72
        const items = data.map((d, i) => ({
          label: String(d[categoryKey] ?? i + 1),
          color: DEFAULT_COLORS[i % DEFAULT_COLORS.length],
        }))
        const legendG = document.createElementNS('http://www.w3.org/2000/svg', 'g')
        items.forEach((item, i) => {
          const y = 20 + i * 18
          const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
          rect.setAttribute('x', String(legendX))
          rect.setAttribute('y', String(y))
          rect.setAttribute('width', '10')
          rect.setAttribute('height', '10')
          rect.setAttribute('fill', item.color)
          legendG.appendChild(rect)
          const text = document.createElementNS('http://www.w3.org/2000/svg', 'text')
          text.setAttribute('x', String(legendX + 14))
          text.setAttribute('y', String(y + 10))
          text.setAttribute('font-size', '10')
          text.setAttribute('fill', '#333')
          text.textContent = item.label
          legendG.appendChild(text)
        })
        svgEl.appendChild(legendG)
      }

      svgMarkup = svgEl.outerHTML
    } else {
      console.warn('[Chart SVG] No <svg> element found in rendered output. Container HTML:', container.innerHTML.substring(0, 500))
    }

    root.unmount()
  } catch (err) {
    console.error('[Chart SVG] Render error:', err)
  } finally {
    document.body.removeChild(container)
  }

  if (!svgMarkup) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><rect width="100%" height="100%" fill="white"/><text x="50%" y="50%" text-anchor="middle" fill="#888">Chart render failed</text></svg>`
  }

  // Build manual legend (Recharts' Legend component doesn't work in hidden DOM)
  let legendSvg = ''
  let legendHeight = 0
  if (showLegend) {
    if (chartType === 'pie' || chartType === 'doughnut') {
      // Pie/doughnut legend: vertical list on the right, injected directly into the chart SVG after render
      // (handled separately below since it overlays the chart area)
    } else if (series.length > 1) {
      // Bar/line/area/scatter: series legend at top
      legendHeight = 20
      const itemWidth = Math.floor(width / series.length)
      legendSvg = series.map((s, i) => {
        const color = s.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]
        const x = i * itemWidth + 10
        return `<rect x="${x}" y="4" width="12" height="12" fill="${color}"/>` +
          `<text x="${x + 16}" y="14" font-size="11" fill="#333">${escapeXml(s.name)}</text>`
      }).join('')
      legendSvg = `<g class="legend">${legendSvg}</g>`
    }
  }

  // Compute total height
  const titleHeight = title ? 24 : 0
  const totalHeight = height + titleHeight + legendHeight

  const bgColor = backgroundColor || 'white'
  const brColor = borderColor || '#d0d0d0'

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${totalHeight}">` +
    `<defs><clipPath id="chartClip"><rect width="100%" height="100%" rx="14" ry="14"/></clipPath></defs>` +
    `<rect width="100%" height="100%" rx="14" ry="14" fill="${bgColor}" stroke="${brColor}" stroke-width="1"/>` +
    `<g clip-path="url(#chartClip)">` +
    (title ? `<text x="${width / 2}" y="16" text-anchor="middle" font-size="14" font-weight="600" fill="#333">${escapeXml(title)}</text>` : '') +
    (legendSvg ? `<g transform="translate(0,${titleHeight})">${legendSvg}</g>` : '') +
    `<g transform="translate(0,${titleHeight + legendHeight})">${svgMarkup}</g>` +
    `</g></svg>`
}

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}
